import { ReleaseRef } from 'hooks/data/versions/types';
import { V4_VERSIONS } from 'utilities/constants/versions';

import { MAX_COMPARE_DATASETS } from './constants';
import { datasetRefKey } from './datasetRef';
import { DatasetRef } from './types';

export const DATASETS_PARAM = 'datasets';

const REF_SEPARATOR = ':';
const LIST_SEPARATOR = ',';

export const encodeCompareDatasets = (refs: DatasetRef[]): string =>
  refs.map(({ id, version, systemModel }) => [id, version, systemModel].join(REF_SEPARATOR)).join(LIST_SEPARATOR);

export type ParsedCompareDatasets = {
  refs: DatasetRef[];
  hasLegacyEntries: boolean;
  invalidEntries: string[];
  droppedEntries: string[];
};

const isValidId = (value: string) => /^\d+$/.test(value);

/** Rejects partial releases: they would resolve against the route's release and fetch the wrong dataset. */
export const parseCompareDatasets = (raw: string | null, fallback: ReleaseRef): ParsedCompareDatasets => {
  const tokens = (raw ?? '')
    .split(LIST_SEPARATOR)
    .map((token) => token.trim())
    .filter(Boolean);

  const refs: DatasetRef[] = [];
  const invalidEntries: string[] = [];
  const droppedEntries: string[] = [];
  const seen = new Set<string>();
  let hasLegacyEntries = false;

  tokens.forEach((token) => {
    const segments = token.split(REF_SEPARATOR).map((segment) => segment.trim());
    let ref: DatasetRef | undefined;

    if (segments.length === 1 && isValidId(segments[0])) {
      hasLegacyEntries = true;
      ref = { id: segments[0], version: fallback.version, systemModel: fallback.system_model };
    } else if (segments.length === 3 && segments.every(Boolean) && isValidId(segments[0])) {
      ref = { id: segments[0], version: segments[1], systemModel: segments[2] };
    }

    if (!ref || V4_VERSIONS.includes(ref.version)) {
      invalidEntries.push(token);
      return;
    }

    const key = datasetRefKey(ref);
    if (seen.has(key)) return;

    if (refs.length >= MAX_COMPARE_DATASETS) {
      droppedEntries.push(token);
      return;
    }

    seen.add(key);
    refs.push(ref);
  });

  return { refs, hasLegacyEntries, invalidEntries, droppedEntries };
};

export const buildComparePath = (tabSlug: string, route: ReleaseRef, refs: DatasetRef[]): string =>
  `/${route.version}/${route.system_model}/compare/${tabSlug}?${DATASETS_PARAM}=${encodeCompareDatasets(refs)}`;
